import {
  CASHIER_ENDPOINTS,
  DEFAULT_CASHIER_BASE_PATH,
  cashierPath,
  type CashierEndpointName,
} from '@pf/bff-contract';
import { ErrorEnvelopeSchema } from '@pf/contracts';
import { z } from 'zod';

/**
 * The console's HTTP layer.
 *
 * Four properties matter here, and each is enforced in one place so no page can opt out:
 *
 * 1. **Every response is parsed against its contract schema.** A page never sees an `any`. If
 *    the server sends a shape the contract does not describe, the query fails loudly with a
 *    named error rather than rendering `undefined` into a table cell.
 * 2. **Every failure is the SAD §8 envelope.** `{code, type, message, retryable, request_id}` is
 *    parsed into an {@link ApiError}, so error UI can show the operator the *actual* reason and
 *    the request id to quote in an incident, instead of "Something went wrong".
 * 3. **Two clients, two base paths.** {@link opsRequest} targets PaymentFlow's operator API and
 *    {@link cashierRequest} targets the participating website's BFF. They are separate functions
 *    precisely so that a customer-area screen calling PF directly is a visible mistake in the
 *    import list (SAD P9, D-I7).
 * 4. **The customer area cannot invent a URL.** {@link cashierRequest} takes an endpoint *name*
 *    from `@pf/bff-contract`'s manifest, not a path — so the method, the path template, and the
 *    response schema all come from the profile. A screen cannot call an endpoint the profile does
 *    not define, which is what keeps "any conforming BFF works" true rather than aspirational.
 *
 * Both base paths and the origin are read from configuration, and every one of them has a working
 * default. Pointing this console at a different BFF is a build-time environment change, never a
 * code change.
 */

/** Origin the clients prefix onto request paths. */
function origin(): string {
  const configured = import.meta.env.VITE_API_ORIGIN;
  if (configured !== undefined && configured !== '') return configured.replace(/\/$/, '');
  if (typeof window !== 'undefined') return window.location.origin;
  return 'http://localhost';
}

/** Base path of PaymentFlow's operator API. */
export function opsBase(): string {
  return import.meta.env.VITE_OPS_BASE ?? '/ops/v1';
}

/**
 * Base path the Cashier Profile is mounted at.
 *
 * Defaults to the profile's own default (`/bff`). A deployment whose website serves the profile
 * somewhere else — a different prefix, or an absolute URL on another origin — sets `VITE_BFF_BASE`
 * and changes nothing in this codebase.
 */
export function bffBase(): string {
  const configured = import.meta.env.VITE_BFF_BASE;
  return configured !== undefined && configured !== '' ? configured : DEFAULT_CASHIER_BASE_PATH;
}

/**
 * True when the app is running against MSW rather than real backends.
 *
 * Two conditions, and the order matters:
 *
 * - `import.meta.env.DEV` is a build-time literal (`true` under `vite`/`vitest`, `false` in any
 *   `vite build`), so in a production bundle this function is the constant `false` and every
 *   mock-only code path behind it is dead code the bundler drops. No `.env*` file, shell
 *   variable, or forgotten flag can turn mocks on in a production build — that happened once
 *   (2026-08-05: a plain `pnpm build` without `VITE_USE_MSW=false` shipped the MSW backend and
 *   the demo-accounts panel to the live console), because the old form of this check defaulted
 *   to mocks-on whenever the variable was simply absent.
 * - `VITE_USE_MSW` still defaults to *on* in dev, so a fresh clone runs the whole console with
 *   no backend at all; `VITE_USE_MSW=false` points dev at the real services instead.
 */
export function usingMocks(): boolean {
  return import.meta.env.DEV && import.meta.env.VITE_USE_MSW !== 'false';
}

/** A failed API call, carrying the SAD §8 error envelope. */
export class ApiError extends Error {
  /** Build the error. */
  constructor(
    readonly status: number,
    readonly code: string,
    readonly type: string,
    message: string,
    readonly retryable: boolean,
    readonly requestId: string | null,
  ) {
    super(message);
    this.name = 'ApiError';
  }

  /** True when the caller is not signed in (or the session lapsed). */
  get isUnauthenticated(): boolean {
    return this.status === 401;
  }

  /** True when the caller is signed in but lacks the role (SAD §11 RBAC). */
  get isForbidden(): boolean {
    return this.status === 403;
  }

  /** True when a guard refused the transition (SAD §4 illegal transition, out of stock, …). */
  get isConflict(): boolean {
    return this.status === 409;
  }
}

/** Raised when a response does not match the contract schema it was promised to satisfy. */
export class ContractViolationError extends Error {
  /** Build the error. */
  constructor(
    readonly path: string,
    readonly issues: string[],
  ) {
    super(`Response from ${path} did not match its contract: ${issues.join('; ')}`);
    this.name = 'ContractViolationError';
  }
}

/** Options for a request. */
export interface RequestOptions {
  /** HTTP method. Defaults to `GET`. */
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  /** JSON request body. */
  body?: unknown;
  /** Query-string parameters; `undefined` and `null` values are omitted. */
  query?: Record<string, string | number | boolean | null | undefined>;
  /** Abort signal, wired up by TanStack Query. */
  signal?: AbortSignal;
}

function buildUrl(base: string, path: string, query: RequestOptions['query']): string {
  const url = new URL(`${base}${path}`, origin());
  for (const [key, value] of Object.entries(query ?? {})) {
    if (value === undefined || value === null || value === '') continue;
    url.searchParams.set(key, String(value));
  }
  return url.toString();
}

/**
 * The envelope *shape* without the closed `code`/`type` enums.
 *
 * Contract endpoints always send a code from `@pf/shared`'s `ERROR_CODES`, and those are parsed
 * strictly. But the console also talks to two surfaces the contract does not cover — the operator
 * session plane (SAD §11 describes it; `/ops/v1`'s OpenAPI document does not) and the
 * participating website's BFF, which is a different system with its own taxonomy. Those legitimately
 * carry codes like `INVALID_TOTP` or `INSUFFICIENT_FUNDS`.
 *
 * So the parse is strict first, lenient second: a real error message and request id reach the
 * operator either way, and an unrecognised code is still surfaced verbatim rather than being
 * flattened into "something went wrong".
 */
const LenientErrorEnvelopeSchema = z.object({
  error: z.object({
    code: z.string().min(1),
    type: z.string().min(1),
    message: z.string().min(1),
    retryable: z.boolean().optional(),
    request_id: z.string().min(1).optional(),
  }),
});

async function parseError(response: Response, path: string): Promise<ApiError> {
  let payload: unknown = null;
  try {
    payload = await response.json();
  } catch {
    payload = null;
  }

  const strict = ErrorEnvelopeSchema.safeParse(payload);
  if (strict.success) {
    const { code, type, message, retryable, request_id } = strict.data.error;
    return new ApiError(response.status, code, type, message, retryable, request_id);
  }

  const lenient = LenientErrorEnvelopeSchema.safeParse(payload);
  if (lenient.success) {
    const { code, type, message, retryable, request_id } = lenient.data.error;
    return new ApiError(
      response.status,
      code,
      type,
      message,
      retryable ?? response.status >= 500,
      request_id ?? null,
    );
  }

  return new ApiError(
    response.status,
    'UNEXPECTED',
    'transient',
    `${String(response.status)} from ${path}`,
    response.status >= 500,
    null,
  );
}

async function request<S extends z.ZodTypeAny>(
  base: string,
  schema: S,
  path: string,
  options: RequestOptions = {},
): Promise<z.infer<S>> {
  const headers: Record<string, string> = { Accept: 'application/json' };
  const init: RequestInit = {
    method: options.method ?? 'GET',
    headers,
    credentials: 'include',
  };
  if (options.signal) init.signal = options.signal;
  if (options.body !== undefined) {
    headers['Content-Type'] = 'application/json';
    init.body = JSON.stringify(options.body);
  }

  const response = await fetch(buildUrl(base, path, options.query), init);
  if (!response.ok) throw await parseError(response, path);

  if (response.status === 204) return schema.parse(undefined) as z.infer<S>;

  const payload: unknown = await response.json();
  const parsed = schema.safeParse(payload);
  if (!parsed.success) {
    throw new ContractViolationError(
      path,
      parsed.error.issues.map((i) => `${i.path.join('.')}: ${i.message}`),
    );
  }
  return parsed.data as z.infer<S>;
}

/** Call PaymentFlow's operator API (`/ops/v1`). Operator area only. */
export function opsRequest<S extends z.ZodTypeAny>(
  schema: S,
  path: string,
  options?: RequestOptions,
): Promise<z.infer<S>> {
  return request(opsBase(), schema, path, options);
}

/**
 * The absolute URL for an `/ops/v1` path, for the one caller that cannot go through
 * {@link opsRequest} — a response that is not JSON has no schema to validate it against.
 *
 * Resolves against {@link origin}, the same way every other request here does: a bare `fetch('/ops/…')`
 * works in a real browser (relative to the document) but not against `undici`'s `fetch`, which
 * `jsdom`'s test environment uses and which has no document to resolve a relative URL against.
 */
export function opsUrl(path: string): string {
  return buildUrl(opsBase(), path, undefined);
}

/** Options for a Cashier Profile call. Method and response schema come from the manifest. */
export interface CashierRequestOptions extends Omit<RequestOptions, 'method'> {
  /** Path parameters for endpoints whose template has them — always `{ id }` in profile v1. */
  params?: Record<string, string>;
}

/** The response type the profile promises for a named endpoint. */
export type CashierResponse<N extends CashierEndpointName> = z.infer<
  (typeof CASHIER_ENDPOINTS)[N]['response']
>;

/**
 * Call the participating website's BFF through the Cashier Profile. Customer area only.
 *
 * Takes an endpoint *name*, never a URL. Everything else — the verb, the path, the schema the
 * response is validated against — is read from `@pf/bff-contract`'s manifest, so this console
 * works against any BFF implementing the profile and cannot drift from it without a type error.
 *
 * SAD P9: end users never call PaymentFlow. If you find yourself wanting `opsRequest` in
 * `src/app/customer/**`, the architecture — not the import — is what needs changing.
 */
export function cashierRequest<N extends CashierEndpointName>(
  name: N,
  options: CashierRequestOptions = {},
): Promise<CashierResponse<N>> {
  const endpoint = CASHIER_ENDPOINTS[name];
  const { params, ...rest } = options;
  return request(bffBase(), endpoint.response, cashierPath(endpoint.path, params ?? {}), {
    ...rest,
    method: endpoint.method,
  });
}
